import { EMPLOYEES, splitSiteVersion } from "@alm/domain/marketing";
import { PEOPLE_ORIGIN } from "./origins";

/** One `@graph` per page (lib/marketing/seo.ts), every node pointing at the Organization by `@id`.
 * Legal name, alternate names, address and founder are stated identically on every page to
 * distinguish PPL AI from People.ai. */

const ORGANIZATION_ID = `${PEOPLE_ORIGIN}/#organization`;
export const WEBSITE_ID = `${PEOPLE_ORIGIN}/#website`;

/* One name in every title, og:site_name and both nodes below: Google shows a
   site name only when they agree. No dash after PEOPLE: results split the name
   there as if the rest were a tagline (seo.test.ts holds the source to it). */
export const SITE_NAME = "PEOPLE AI Workforce";

/* The short form, which is also the legal name and the domain, first. */
const ALTERNATE_NAMES = ["PPL AI", "PPL", "PEOPLE"];

/** The company's own profiles elsewhere: LinkedIn, the Facebook Page and the
 *  Google Business Profile (its share link). Only addresses that exist: a
 *  placeholder published as a URL is a broken link in the knowledge graph. */
const SAME_AS = [
  "https://www.linkedin.com/company/ppl-ai-al",
  "https://www.facebook.com/profile.php?id=61594916214773",
  "https://share.google/EuZUVc01ikrU8Vdyf",
];

export const ORGANIZATION = {
  "@type": "Organization",
  "@id": ORGANIZATION_ID,
  /* "PPL" alone collides with other names in search and "People AI" alone is
     People.ai's: the short forms are alternates, never the name, and bare
     "People AI" is not one of them. */
  name: SITE_NAME,
  alternateName: ALTERNATE_NAMES,
  legalName: "PPL AI Inc.",
  url: `${PEOPLE_ORIGIN}/`,
  /* A square 512px mark, as Google's guidance asks (at least 112px, square). */
  logo: `${PEOPLE_ORIGIN}/ppl-logo.png`,
  email: "info@ppl-ai.com",
  telephone: "+1-954-702-3000",
  address: {
    "@type": "PostalAddress",
    streetAddress: "1515 Rice Road",
    addressLocality: "Tyler",
    addressRegion: "TX",
    postalCode: "75703",
    addressCountry: "US",
  },
  areaServed: "US",
  founder: { "@type": "Person", name: "Ashok Gupta" },
  sameAs: SAME_AS,
  description:
    "PEOPLE AI Workforce (PPL AI) is the AI workforce for assisted living: AI employees that run a community's administrative company on one senior-living operating system.",
} as const;

export const WEBSITE = {
  "@type": "WebSite",
  "@id": WEBSITE_ID,
  url: `${PEOPLE_ORIGIN}/`,
  name: SITE_NAME,
  alternateName: ALTERNATE_NAMES,
  inLanguage: "en",
  publisher: { "@id": ORGANIZATION_ID },
} as const;

/** Points at the Organization from a page's own node. */
export const PUBLISHER = { "@id": ORGANIZATION_ID } as const;

export interface Crumb {
  name: string;
  /** Site path, e.g. "/workforce". */
  path: string;
}

const HOME: Crumb = { name: "Home", path: "/" };

/** The trail from the home page to this one. Home is always first and is not passed. */
export function breadcrumbs(...trail: Crumb[]) {
  return {
    "@type": "BreadcrumbList",
    itemListElement: [HOME, ...trail].map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.name,
      item: `${PEOPLE_ORIGIN}${crumb.path}`,
    })),
  };
}

export interface PageFacts {
  /** Site path, e.g. "/solutions". */
  path: string;
  name: string;
  description: string;
}

/** A page with no more specific type: what it is called, what it says, and that it is part of this
 * site. */
function webPage({ path, name, description }: PageFacts) {
  const url = `${PEOPLE_ORIGIN}${path}`;
  return {
    "@type": "WebPage",
    "@id": `${url}#webpage`,
    url,
    name,
    description,
    inLanguage: "en",
    isPartOf: { "@id": WEBSITE_ID },
    publisher: PUBLISHER,
  };
}

/** The WebPage node for a page described the way `marketingHead` is: the same
 *  title and description, and the route's path with the arm prefix removed. */
export function webPageOf({
  title,
  description,
  path,
}: {
  title: string;
  description?: string;
  path: string;
}) {
  const { basePath } = splitSiteVersion(path);
  return webPage({ path: basePath, name: title, description: description ?? title });
}

type Employee = (typeof EMPLOYEES)[number];

const AUDIENCE = { "@type": "BusinessAudience", name: "Assisted living operators" } as const;

/** The desk page, as the service it sells: one AI employee, in one role. */
export function employeeService(employee: Employee) {
  const role = employee.role.startsWith("AI ") ? employee.role : `AI ${employee.role}`;
  return {
    "@type": "Service",
    "@id": `${PEOPLE_ORIGIN}/workforce/${employee.slug}#service`,
    name: `${employee.name}: ${role}`,
    serviceType: role,
    url: `${PEOPLE_ORIGIN}/workforce/${employee.slug}`,
    description: employee.description,
    provider: PUBLISHER,
    audience: AUDIENCE,
  };
}

/** The roster page: the nine desks, in the order the site lists them. */
export const WORKFORCE_LIST = {
  "@type": "ItemList",
  name: "The AI workforce",
  itemListOrder: "https://schema.org/ItemListOrderAscending",
  numberOfItems: EMPLOYEES.length,
  itemListElement: EMPLOYEES.map((employee, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: employee.name,
    url: `${PEOPLE_ORIGIN}/workforce/${employee.slug}`,
  })),
} as const;
